// src/pages/AuthCallback.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";

const LS_POST_LOGIN = "pwp_post_login_path";
const LS_PKCE_RETRY = "pwp_pkce_retry_once"; // prevents infinite loops

function sanitizeInternalPath(path, fallback = "/pwp-paywall?next=/level-2") {
  if (typeof path !== "string") return fallback;
  if (!path.startsWith("/")) return fallback;
  if (path.startsWith("//")) return fallback;
  return path;
}

function parseHashParams(hash) {
  const h = (hash || "").replace(/^#/, "");
  return new URLSearchParams(h);
}

function isPkceVerifierMissingError(message) {
  const m = String(message || "").toLowerCase();
  return (
    m.includes("pkce code verifier not found") ||
    m.includes("code verifier not found") ||
    m.includes("code_verifier") ||
    m.includes("verifier not found in storage")
  );
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const [msg, setMsg] = useState("Finishing login…");

  useEffect(() => {
    let alive = true;

    // ✅ Compute target OUTSIDE try/catch so catch can use it too
    const searchParams = new URLSearchParams(location.search || "");
    const stored = sessionStorage.getItem(LS_POST_LOGIN);
    const back = searchParams.get("back");
    const target = sanitizeInternalPath(
      stored || back || "/pwp-paywall?next=/level-2",
      "/pwp-paywall?next=/level-2"
    );

    // fail-safe: never hang on a blank screen forever
    const timer = setTimeout(() => {
      if (!alive) return;
      setMsg("Still working… if this takes too long, tap Back and try again.");
    }, 8000);

    async function run() {
      try {
        const code = searchParams.get("code");

        // fallback if anything ever comes via hash
        const hashParams = parseHashParams(location.hash || "");
        const access_token = hashParams.get("access_token");
        const refresh_token = hashParams.get("refresh_token");

        /**
         * ✅ If user already has a session, DO NOT attempt exchangeCodeForSession again.
         * This avoids PKCE errors when session already exists.
         */
        setMsg("Checking existing session…");
        const pre = await supabase.auth.getSession();
        if (pre?.data?.session) {
          sessionStorage.removeItem(LS_POST_LOGIN);
          sessionStorage.removeItem(LS_PKCE_RETRY);
          if (!alive) return;
          navigate(target, { replace: true });
          return;
        }

        // 1) PKCE: exchange code for session
        if (code) {
          setMsg("Exchanging login code…");
          const { error } = await supabase.auth.exchangeCodeForSession(
            window.location.href
          );
          if (error) throw new Error(error.message);
        }

        // 2) Implicit fallback: set session from hash tokens
        if (!code && access_token && refresh_token) {
          setMsg("Setting session…");
          const { error } = await supabase.auth.setSession({
            access_token,
            refresh_token,
          });
          if (error) throw new Error(error.message);
        }

        // 3) Confirm session exists
        setMsg("Confirming session…");
        const { data, error } = await supabase.auth.getSession();
        if (error) throw new Error(error.message);
        if (!data?.session) throw new Error("Session not found after login.");

        // cleanup after success
        sessionStorage.removeItem(LS_POST_LOGIN);
        sessionStorage.removeItem(LS_PKCE_RETRY);

        if (!alive) return;
        navigate(target, { replace: true });
      } catch (e) {
        if (!alive) return;

        const message = e?.message || "Could not finish login.";

        /**
         * ✅ If PKCE verifier is missing, auto-bounce to /login ONCE.
         * Preserve target so user returns to the correct place.
         */
        if (isPkceVerifierMissingError(message)) {
          const alreadyRetried = sessionStorage.getItem(LS_PKCE_RETRY) === "1";
          if (!alreadyRetried) {
            sessionStorage.setItem(LS_PKCE_RETRY, "1");
            setMsg("Login needs one more try… redirecting to Login.");
            setTimeout(() => {
              if (!alive) return;
              navigate(`/login?back=${encodeURIComponent(target)}`, {
                replace: true,
              });
            }, 600);
            return;
          }

          setMsg(
            "Login didn’t complete (browser storage blocked/cleared). Tap Back to Login and try again — if it still fails, use Email OTP."
          );
          return;
        }

        setMsg(message);
      } finally {
        clearTimeout(timer);
      }
    }

    run();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [navigate, location.search, location.hash]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border rounded-2xl shadow p-6 text-center">
        <div className="text-2xl font-extrabold text-purple-700">
          🔐 Logging you in…
        </div>
        <div className="mt-3 text-sm text-slate-600">{msg}</div>

        <button
          className="mt-5 w-full rounded-xl border py-3 font-bold bg-white hover:bg-gray-50 transition"
          onClick={() => navigate("/login", { replace: true })}
        >
          Back to Login
        </button>
      </div>
    </div>
  );
}